import { ArrowDown, CircleAlert, FoldVertical, Info, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * The notes the composer shows above its footer: a model pick lowered the thinking level, the
 * chat's model is unavailable, a compaction runs (`compacting`), and how this tab's press ended:
 * compacted, a note (`info`) or a refusal or failure (`alert`).
 */
export type ComposerNoteKind =
  | 'lowered'
  | 'unavailable'
  | 'compacting'
  | 'compacted'
  | 'info'
  | 'alert';

const ICONS = {
  lowered: ArrowDown,
  unavailable: CircleAlert,
  compacting: Loader2,
  compacted: FoldVertical,
  info: Info,
  alert: CircleAlert,
} as const;

interface ComposerNoteProps {
  kind: ComposerNoteKind;
  /** The note's sentence. */
  text: string;
  /** Its action's name (Undo, Choose model); a note without one has none. */
  actionLabel?: string;
  onAction?: () => void;
  /** Absent: the note cannot be dismissed (a compaction running). */
  onDismiss?: () => void;
}

/**
 * One row inside the composer box, between the attachments and the footer (DEV-4575 Option C):
 * an icon, the sentence followed by its action, and Dismiss, in the drawn 12 px. It takes the
 * textarea's room, so the box keeps its height. The sentence is never cut: where one 24 px line
 * is too short for it (the side panel at its default width), it wraps, its action after it.
 * Composer renders it inside a polite live region, so its arrival is read out.
 */
export function ComposerNote({ kind, text, actionLabel, onAction, onDismiss }: ComposerNoteProps) {
  const { t } = useTranslation('ai-cio');
  const Icon = ICONS[kind];
  return (
    <div className="mb-2.5 flex min-h-6 items-center gap-1.5 px-3 text-xs text-foreground">
      {kind === 'compacting' ? (
        // The spinner, 14 px, in the 16 px icon's place.
        <span className="flex size-4 shrink-0 items-center justify-center">
          <Icon aria-hidden className="size-3.5 animate-spin text-muted-foreground" />
        </span>
      ) : (
        <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      )}
      <p className="min-w-0 flex-1">
        {actionLabel && onAction ? (
          <>
            {/* The space after the sentence ends its line when the action wraps. */}
            <span className="me-1.5">{text}</span>{' '}
            <button
              type="button"
              onClick={onAction}
              className="whitespace-nowrap font-medium hover:underline"
            >
              {actionLabel}
            </button>
          </>
        ) : (
          text
        )}
      </p>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('composer.dismiss', 'Dismiss')}
          className="ms-auto flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}
